import { Box, Typography, Paper, Grid } from '@mui/material';
import { BarChart, PieChart } from '@mui/x-charts-pro';
import { SectionHeader } from './DashboardCard';

type ChartData = { label: string; value: number }[];

export const HeadcountByDepartment = ({ data }: { data: ChartData }) => (
  <Paper sx={{ p: 3 }}>
    <SectionHeader title="Headcount by Department" />
    <Box sx={{ height: 280 }}>
      <BarChart xAxis={[{ scaleType: 'band', data: data.map(d => d.label) }]} series={[{ data: data.map(d => d.value) }]} height={260} />
    </Box>
  </Paper>
);

export const DemographicsPanel = ({ raceData, genderData }: { raceData: ChartData; genderData: ChartData }) => {
  const raceColors = ['primary.main', 'info.main', 'warning.main', 'secondary.main'];
  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Workforce Demographics</Typography>
      <Box sx={{ display: 'flex', gap: 2, height: 140 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary">By Race</Typography>
          <Box sx={{ height: 110 }}>
            <PieChart series={[{ data: raceData, innerRadius: 30, outerRadius: 50 }]} height={110} />
          </Box>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary">By Gender</Typography>
          <Box sx={{ height: 110 }}>
            <PieChart series={[{ data: genderData, innerRadius: 30, outerRadius: 50 }]} height={110} />
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
        {raceData.map((r, i) => (
          <Box key={r.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: raceColors[i] }} />
            <Typography variant="caption">{r.label}: {r.value}</Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export const OnboardingByStageChart = ({ data }: { data: ChartData }) => (
  <Paper sx={{ p: 3 }}>
    <SectionHeader title="Onboarding by Stage" actionHref="/onboarding-processes" actionLabel="View All" />
    <Box sx={{ height: 200 }}>
      <BarChart xAxis={[{ scaleType: 'band', data: data.map(s => s.label) }]} series={[{ data: data.map(s => s.value) }]} height={180} />
    </Box>
  </Paper>
);

export const TrainingStatusChart = ({ data }: { data: ChartData }) => {
  const colors = ['success.main', 'info.main', 'default.main'];
  return (
    <Paper sx={{ p: 3 }}>
      <SectionHeader title="Training Status" actionHref="/training-records" actionLabel="View All" />
      <Box sx={{ height: 200 }}>
        <PieChart series={[{ data, innerRadius: 30, outerRadius: 70 }]} height={180} />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
        {data.map((s, i) => (
          <Box key={s.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colors[i] }} />
            <Typography variant="caption">{s.label}: {s.value}</Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export const DisciplinaryByStageChart = ({ data }: { data: ChartData }) => (
  <Paper sx={{ p: 3 }}>
    <SectionHeader title="Disciplinary Cases" actionHref="/disciplinary-cases" actionLabel="View All" />
    <Box sx={{ height: 200 }}>
      <BarChart xAxis={[{ scaleType: 'band', data: data.map(s => s.label) }]} series={[{ data: data.map(s => s.value) }]} height={180} />
    </Box>
  </Paper>
);

export const InvoiceStatusChart = ({ data }: { data: ChartData }) => {
  const colors = ['success.main', 'info.main', 'error.main'];
  return (
    <Paper sx={{ p: 3 }}>
      <SectionHeader title="Invoice Status" />
      <Box sx={{ height: 180 }}>
        <PieChart series={[{ data, innerRadius: 30, outerRadius: 70 }]} height={160} />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
        {data.map((s, i) => (
          <Box key={s.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colors[i] }} />
            <Typography variant="caption">{s.label}: {s.value}</Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export const DSARStatusChart = ({ data }: { data: ChartData }) => {
  const colors = ['warning.main', 'info.main', 'success.main'];
  return (
    <Paper sx={{ p: 3 }}>
      <SectionHeader title="DSAR Requests" actionHref="/dsar-requests" actionLabel="View All" />
      <Box sx={{ height: 180 }}>
        <PieChart series={[{ data, innerRadius: 30, outerRadius: 70 }]} height={160} />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
        {data.map((s, i) => (
          <Box key={s.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colors[i] }} />
            <Typography variant="caption">{s.label}: {s.value}</Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};
